import type { SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import type { FormControl } from '@angular/forms';

import { LetDirective } from '@ngrx/component';
import type { Observable } from 'rxjs';

import type { CurrentUser, User } from '@core/account-types';
import { TranslatePipe } from '@core/i18n-legacy';
import type { Select2GroupWithItems } from '@core/user-interface-legacy';
import { UnifiedJobFull } from '@recruitee/job-editors-shared-types';
import type {
  MailboxAASenderSelectItem,
  MailboxAASenderSelectValue,
  MailboxSelectItem,
} from '@recruitee/mailbox-ui';
import { MailboxSelectComponent } from '@recruitee/mailbox-ui';

import { AutomatedActionsEmailSenderSelect } from './automated-actions-email-sender-select.service';

@Component({
  selector: 'rt-automated-actions-email-sender-select',
  template: `
    <ng-container *ngrxLet="viewModel$ as vm">
      @if (vm.items) {
        <rt-mailbox-select
          [header]="'app.shared_mailboxes.aa_sender_select.header' | translate"
          [control]="control"
          [disabled]="readonly"
          [currentUser]="vm.currentUser!"
          [users]="vm.users!"
          [groupedItems]="vm.items"
          [triggerSize]="'small'"
        />
      }
    </ng-container>
  `,
  imports: [MailboxSelectComponent, LetDirective, TranslatePipe],
  viewProviders: [AutomatedActionsEmailSenderSelect],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutomatedActionsEmailSenderSelectComponent {
  @Input() public itemFilterFn: (item: MailboxAASenderSelectItem) => boolean;
  @Input() public job?: Pick<UnifiedJobFull, 'id' | 'hiringManagerId' | 'recruiterId'>;
  @Input({ required: true }) public control: FormControl<MailboxAASenderSelectValue | null>;
  @Input() public readonly = false;

  public viewModel$: {
    currentUser: Observable<CurrentUser>;
    users: Observable<Record<string, User | null>>;
    items: Observable<Array<Select2GroupWithItems<MailboxSelectItem>>>;
  };

  private mailboxSelectService = inject(AutomatedActionsEmailSenderSelect);

  constructor() {
    this.viewModel$ = {
      currentUser: this.mailboxSelectService.currentUser$,
      users: this.mailboxSelectService.users$,
      items: this.mailboxSelectService.mailboxItems$,
    };
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('itemFilterFn' in changes) {
      this.mailboxSelectService.setFilterFn(this.itemFilterFn);
      this.mailboxSelectService.refreshItems();
    }
    if ('job' in changes) {
      this.mailboxSelectService.setJob(this.job!);
    }
  }
}
